import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useFuzzySearchList } from '@nozbe/microfuzz/react';

import {
  FloatingFocusManager,
  FloatingList,
  useFloating,
  useInteractions,
  useListNavigation,
} from '@floating-ui/react';

import { useTrackEvent } from '@/lib/analytics';
import { useIsScrolled } from '@/lib/hooks/use-is-scrolled';
import {
  buildExplorationFromCells,
  buildModelExploration,
  getCleanedExplorationName,
  getExplorationSections,
  hasExplorationSection,
} from '@/core/exploration';
import { isReplicableCell, ReplicableCell } from '@/core/cell';
import { useBuildAccountUrl } from '@/lib/accounts/context';
import { notNil } from '@/lib/utils';

import { Icon } from '../../components/icon';
import { Modal } from '../../components/modal';
import { Panel } from '../../components/panel';
import { NoMatchBanner } from '../../components/banner';
import { Cell, CellWithPipeline, Exploration, Model, ModelKind } from '../types';
import { buildExplorationHashUrl, buildExplorationUrl, isPersistedBaseExploration } from '../utils';
import { CollapsibleSection } from './collapsible-section';
import { CellList } from './cell-list';
import { ExplorationList } from './exploration-list';
import { dereferencePipeline } from '../pipeline/utils';
import { extractCellFromExploration } from '../exploration/utils';
import { getModelOrThrow, getModelKind } from '../model/utils';
import { CellListNode, ExplorationListNode, ExplorationNode, flattenLists } from '.';
import { InlineButton } from '../../components/button';
import { Tip } from '../../components/tip';
import { useMetadataContext } from '../metadata-context';

import styles from './exploration-search.module.scss';

const modelKindSectionTitles: { [key in ModelKind]: string } = {
  Account: 'Accounts',
  User: 'Users',
  Event: 'Events',
  Entity: 'Entities',
};

const isNotIgnoredCell = (cell: CellWithPipeline, ignoredPipelineIds: string[] = []) =>
  ignoredPipelineIds.length === 0 ||
  (cell.pipeline.pipelineId !== undefined &&
    !ignoredPipelineIds.includes(cell.pipeline.pipelineId));

interface PipelineSearchProps {
  exploration?: Exploration;
  searchExplorations?: boolean;
  onSelectModel: (model: Model) => void;
  onSelectCell: (cell: Cell, dependencies: Cell[]) => void;
  kinds?: ModelKind[];
  title?: string;
  className?: string;
  ignoredPipelineIds?: string[];
}

const PipelineSearch = (props: PipelineSearchProps) => {
  const {
    exploration,
    onSelectModel,
    onSelectCell,
    kinds,
    className,
    searchExplorations = true,
    ignoredPipelineIds,
  } = props;
  const { models, explorations } = useMetadataContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [showOtherModels, setShowOtherModels] = useState(false);
  const otherModelsShown = useRef(false);
  const [isScrolled, scrollContainerRef] = useIsScrolled<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [collapsedLists, setCollapsedLists] = useState<Record<number, boolean>>({});
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const otherModelsContainerRef = useRef<HTMLDivElement>(null);
  const trackEvent = useTrackEvent();
  const buildAccountUrl = useBuildAccountUrl();

  const toggleItemExpand = (id: string) => {
    setExpandedItems((expandedItems) => {
      const set = new Set(expandedItems);
      set.has(id) ? set.delete(id) : set.add(id);
      return set;
    });
  };

  const trimmedSearchTerm = searchTerm.trim().toLocaleLowerCase();

  const savedExplorations = useFuzzySearchList<Exploration, Exploration>({
    strategy: 'smart',
    list: useMemo(
      () => explorations.filter(() => searchExplorations).filter(isPersistedBaseExploration),
      [explorations, searchExplorations],
    ),
    queryText: trimmedSearchTerm,
    getText: useCallback((option: Exploration) => [getCleanedExplorationName(option.name)], []),
    mapResultItem: useCallback(({ item }) => item, []),
  });

  const modelExplorations = useFuzzySearchList<Model, Exploration>({
    strategy: 'smart',
    list: models,
    queryText: trimmedSearchTerm,
    getText: useCallback((option: Model) => [option.name], []),
    mapResultItem: useCallback(({ item }) => buildModelExploration(item), []),
  });

  const classifiedModelExplorations = useMemo(
    () =>
      modelExplorations.filter((exploration) => {
        const kind = getModelKind(exploration);
        return (kind !== null && kinds?.includes(kind)) ?? false;
      }),
    [kinds, modelExplorations],
  );

  const unclassifiedModelExplorations = useMemo(
    () =>
      modelExplorations.filter(
        (exploration) => classifiedModelExplorations.indexOf(exploration) === -1,
      ),
    [classifiedModelExplorations, modelExplorations],
  );

  const savedExplorationsWithReplicableCells = useMemo(
    () =>
      savedExplorations.filter(
        (exploration) => exploration.view.cells.filter(isReplicableCell).length > 0,
      ),
    [savedExplorations],
  );

  const modelSections = useMemo(
    () => getExplorationSections(unclassifiedModelExplorations),
    [unclassifiedModelExplorations],
  );

  const modelsWithoutSections = useFuzzySearchList<Exploration, Exploration>({
    strategy: 'smart',
    list: useMemo(
      () =>
        unclassifiedModelExplorations.filter((exploration) => !hasExplorationSection(exploration)),
      [unclassifiedModelExplorations],
    ),
    queryText: trimmedSearchTerm,
    getText: useCallback((option: Exploration) => [option.name], []),
    mapResultItem: useCallback(({ item }) => item, []),
  });

  const onThisPageCells = useFuzzySearchList<ReplicableCell, ReplicableCell>({
    strategy: 'smart',
    list: useMemo(
      () =>
        exploration?.view.cells
          .filter(isReplicableCell)
          .filter((cell) => isNotIgnoredCell(cell, ignoredPipelineIds)) ?? [],
      [exploration?.view.cells, ignoredPipelineIds],
    ),
    queryText: searchTerm.trim(),
    getText: useCallback(
      (option: ReplicableCell) => [('title' in option ? option.title : '') ?? ''],
      [],
    ),
    mapResultItem: useCallback(({ item }) => item, []),
  });

  const hasClassifiedModels = classifiedModelExplorations.length > 0;
  const hasUnclassifiedModels =
    savedExplorationsWithReplicableCells.length > 0 ||
    unclassifiedModelExplorations.length > 0 ||
    modelsWithoutSections.length > 0;

  const otherModelsButtonVisible = hasUnclassifiedModels && hasClassifiedModels;
  const otherModelsVisible = hasUnclassifiedModels && (!hasClassifiedModels || showOtherModels);

  useEffect(() => {
    trackEvent('Pipeline Search Opened');
  }, [trackEvent]);

  useEffect(() => {
    if (trimmedSearchTerm.length === 0) {
      return;
    }
    const timeout = setTimeout(() => {
      trackEvent('Pipeline Search', {
        searchTerm: trimmedSearchTerm,
        modelCount: modelExplorations.length,
      });
    }, 500);
    return () => clearTimeout(timeout);

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedSearchTerm]);

  useEffect(() => {
    if (showOtherModels && !otherModelsShown.current) {
      otherModelsContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    otherModelsShown.current = showOtherModels;
  }, [showOtherModels]);

  const handleModelExplorationClick = useCallback(
    (exploration: Exploration) => {
      if (!('pipeline' in exploration.view.cells[0])) {
        throw new Error('Cell does not have a pipeline');
      }
      const dereferencedPipeline = dereferencePipeline(
        exploration.view.cells[0].pipeline,
        exploration,
      );
      const model = getModelOrThrow(models, dereferencedPipeline.baseModelId);

      trackEvent('Pipeline Search Clicked Model', {
        model,
      });

      onSelectModel(model);
    },
    [models, onSelectModel, trackEvent],
  );

  const handleCellClick = useCallback(
    (cell: Cell, dependencies: Cell[]) => {
      trackEvent('Pipeline Search Clicked Cell', {
        cell,
        dependencies,
      });

      onSelectCell(cell, dependencies);
    },
    [onSelectCell, trackEvent],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); //  Prevent explicit newlines in textarea
      flattenedItems.at(activeIndex !== null && activeIndex !== -1 ? activeIndex : 0)?.onClick?.();
    }
  };

  const handleToggleOtherModels = () => {
    setShowOtherModels(!showOtherModels);
    inputRef.current?.focus();
  };

  const classifiedLists: (ExplorationListNode | null)[] = useMemo(
    () =>
      (kinds ?? []).map((kind) => {
        const explorations = classifiedModelExplorations.filter(
          (exploration) => getModelKind(exploration) === kind,
        );
        if (explorations.length === 0) {
          return null;
        }
        return {
          title: modelKindSectionTitles[kind] ?? kind,
          children: explorations.map<ExplorationNode>((exploration) => {
            const getUrl = () => buildAccountUrl(buildExplorationUrl(exploration));
            return {
              exploration,
              getUrl,
              onClick: (event) => {
                event?.preventDefault();
                handleModelExplorationClick(exploration);
              },
            };
          }),
        };
      }),
    [buildAccountUrl, classifiedModelExplorations, handleModelExplorationClick, kinds],
  );

  const onThisPageList: CellListNode | null = useMemo(
    () =>
      exploration === undefined || onThisPageCells?.length === 0
        ? null
        : {
            title: 'On This Page',
            exploration,
            children: onThisPageCells.map((cell) => {
              const getExtractedCell = () => {
                return extractCellFromExploration(cell.id, exploration);
              };
              const getOutpuExploration = () => {
                const { cell: extractedCell, dependencies } = getExtractedCell();
                return buildExplorationFromCells([extractedCell, ...dependencies]);
              };
              const getUrl = () => buildAccountUrl(buildExplorationHashUrl(getOutpuExploration()));
              return {
                cell,
                getUrl,
                onClick: (event) => {
                  event?.preventDefault();
                  const { cell: extractedCell, dependencies } = getExtractedCell();
                  handleCellClick(extractedCell, dependencies);
                },
              };
            }),
          },
    [buildAccountUrl, exploration, handleCellClick, onThisPageCells],
  );

  const unclassifiedLists: (ExplorationListNode | null)[] = useMemo(
    () => [
      otherModelsVisible && savedExplorationsWithReplicableCells.length
        ? {
            title: 'Saved Explorations',
            children: savedExplorationsWithReplicableCells.map((exploration) => {
              const getUrl = () => buildAccountUrl(buildExplorationUrl(exploration));
              return {
                exploration,
                getUrl,
                onClick: (event) => {
                  event?.preventDefault();
                  toggleItemExpand(exploration.explorationId);
                },
                children: exploration.view.cells
                  .filter(isReplicableCell)
                  .map((cell) => {
                    try {
                      const { cell: extractedCell, dependencies } = extractCellFromExploration(
                        cell.id,
                        exploration,
                      );
                      const outputExploration = buildExplorationFromCells([
                        extractedCell,
                        ...dependencies,
                      ]);
                      const getUrl = () =>
                        buildAccountUrl(buildExplorationHashUrl(outputExploration));
                      return {
                        cell,
                        getUrl,
                        onClick: (event: MouseEvent) => {
                          event?.preventDefault();
                          handleCellClick(extractedCell, dependencies);
                        },
                      };
                    } catch {
                      return null;
                    }
                  })
                  .filter(notNil),
              };
            }),
          }
        : null,
      ...(otherModelsVisible
        ? modelSections.map<ExplorationListNode>((section) => ({
            title: section,
            children: unclassifiedModelExplorations
              .filter(({ labels }) => labels.section === section)
              .map((exploration) => {
                const getUrl = () => buildAccountUrl(buildExplorationUrl(exploration));
                return {
                  exploration,
                  getUrl,
                  onClick: (event) => {
                    event?.preventDefault();
                    handleModelExplorationClick(exploration);
                  },
                };
              }),
          }))
        : []),
      otherModelsVisible
        ? {
            title: modelSections.length > 0 ? 'Other Data Models' : 'Data Models',
            children: modelsWithoutSections.map((exploration) => {
              const getUrl = () => buildAccountUrl(buildExplorationUrl(exploration));
              return {
                exploration,
                getUrl,
                onClick: (event) => {
                  event?.preventDefault();
                  handleModelExplorationClick(exploration);
                },
              };
            }),
          }
        : null,
    ],
    [
      buildAccountUrl,
      handleCellClick,
      handleModelExplorationClick,
      modelSections,
      modelsWithoutSections,
      otherModelsVisible,
      savedExplorationsWithReplicableCells,
      unclassifiedModelExplorations,
    ],
  );
  const onThisPageIndex = classifiedLists.length ?? 0;
  const flattenedItems = flattenLists([...classifiedLists, onThisPageList, ...unclassifiedLists]);

  const { refs, context } = useFloating({
    open: true,
  });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
    loop: true,
    onNavigate: (index) => setActiveIndex(index),
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNavigation]);

  const renderList = (list: ExplorationListNode | null, index: number) => {
    if (list === null) {
      return null;
    }
    const shouldBeCollapsedByDefault = list.title === 'Saved Explorations';
    const isCollapsed = collapsedLists[index] ?? shouldBeCollapsedByDefault;
    return (
      <CollapsibleSection
        key={index}
        title={list.title ?? 'Models'}
        isCollapsed={isCollapsed}
        onToggleCollapsed={() => setCollapsedLists({ ...collapsedLists, [index]: !isCollapsed })}>
        <ExplorationList
          items={list.children}
          activeIndex={activeIndex}
          getItemProps={getItemProps}
          disabled={isCollapsed}
          expandedItems={expandedItems}
        />
      </CollapsibleSection>
    );
  };

  return (
    <Panel className={classNames(styles.panel, className)}>
      <section className={styles.explorationSearch} ref={refs.setFloating} {...getFloatingProps()}>
        <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
          <FloatingList elementsRef={listRef}>
            <div className={styles.searchHeader}>
              <h1>{props.title ?? 'Select Model'}</h1>
              <div className={styles.searchInput}>
                <Icon name="Search" size={24} className={styles.searchIcon} />
                <input
                  ref={refs.setReference}
                  value={searchTerm}
                  placeholder="Search your data models..."
                  autoFocus
                  {...getReferenceProps({
                    onKeyDown: handleKeyDown,
                    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setSearchTerm(e.currentTarget.value);
                    },
                  })}
                />
              </div>
            </div>
            <div
              className={classNames(styles.allExplorations, {
                [styles.scrolled]: isScrolled,
              })}
              ref={scrollContainerRef}>
              {!hasClassifiedModels && (
                <Tip disableKey={'tip.modelKinds.disabledAt'}>
                  Did you know you can add more context to your data models by labeling them as
                  events, users, accounts, or entities? Doing so helps Supersimple provide smarter
                  suggestions in the future.
                </Tip>
              )}
              {classifiedLists.map(renderList)}

              {exploration !== undefined && !isNil(onThisPageList) && (
                <CollapsibleSection
                  title="On This Page"
                  isCollapsed={collapsedLists[onThisPageIndex] ?? true}
                  onToggleCollapsed={() =>
                    setCollapsedLists({
                      ...collapsedLists,
                      [onThisPageIndex]: !(collapsedLists[onThisPageIndex] ?? true),
                    })
                  }>
                  <CellList
                    exploration={exploration}
                    items={onThisPageList.children}
                    activeIndex={activeIndex}
                    disabled={collapsedLists[onThisPageIndex] ?? true}
                    getItemProps={getItemProps}
                  />
                </CollapsibleSection>
              )}

              {otherModelsButtonVisible && (
                <>
                  <hr />
                  <InlineButton
                    className={styles.otherModelsButton}
                    onClick={handleToggleOtherModels}>
                    {showOtherModels ? 'Hide other data models' : 'Other data models'}
                  </InlineButton>
                </>
              )}

              {otherModelsVisible && (
                <div ref={otherModelsContainerRef}>
                  {unclassifiedLists.map((list, i) => renderList(list, onThisPageIndex + 1 + i))}
                </div>
              )}

              {flattenedItems.length === 0 && <NoMatchBanner />}
            </div>
          </FloatingList>
        </FloatingFocusManager>
      </section>
    </Panel>
  );
};

interface PipelineSearchModalProps extends PipelineSearchProps {
  onClose: () => void;
}

export const PipelineSearchModal = ({ onClose, ...rest }: PipelineSearchModalProps) => (
  <Modal onClose={onClose} closeOnClickAway>
    <PipelineSearch {...rest} />
  </Modal>
);
